import {useRouteMatches} from '@github-ui/react-core/future/query-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link, Outlet, useParams} from '@github-ui/react-router'
import {updateCurrentApp} from '@github-ui/stats'
import {codeViewRepoRoute} from '@github-ui/code-view/routes/code-view-repo-route'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {AnalyticsContext} from '@github-ui/analytics-provider/context'

import {repoLayoutRoute} from '../routes/repo-layout-route'
import {assetsForRoutes} from '../routes/repo-layout-payload'
import {useSyncActiveNavTab} from '../routes/nav-tab'
import {useGlobalAssets} from './global-scripts'
import {use, useEffect, type ReactNode} from 'react'
import {useSubAppName} from '../hooks/use-sub-app-name'
import {useCrossAppNavigationStat} from '../hooks/use-cross-app-navigation-stat'
import {usePreloadVisibleRouteComponents} from '../hooks/use-preload-visible-route-components'

// Renders the global bundles the active route(s) need, mapped to the version-safe fingerprinted URLs
// from the layout payload. Everything here is client-only: useGlobalAssets suppresses the server
// snapshot, so `renderToString` (Alloy SSR) emits nothing and Rails owns the hard-load paint. After
// hydration React renders the tags declaratively and hoists + dedupes them.
//
// - JS: React only dedupes an async script against `script[async][src]`, which Rails' `<script defer>`
//   tags don't match, so useGlobalAssets drops bundles already on the page; only what a soft nav
//   introduces is added (and kept as a resource across route changes).
// - CSS: React dedupes stylesheets by href and adopts Rails' existing `<link>`, so no filter is needed
//   — but SSR is still gated so we don't emit a *second* `<link>` into the app fragment (which would
//   also risk shifting cascade order). We mirror Rails' `crossorigin`/`media="all"` so a link React
//   creates on a soft nav matches the hard-load ones.
function GlobalAssets() {
  const {data} = useRouteQuery(repoLayoutRoute, 'mainQuery')
  const matches = useRouteMatches()

  const {js, css} = assetsForRoutes(
    matches.map(match => match.handle?.queryRoute?.id),
    data,
  )
  const {scriptSrcs, styleHrefs, crossOrigin} = useGlobalAssets(js, css)

  return (
    <>
      {styleHrefs.map(href => (
        <link key={href} rel="stylesheet" href={href} media="all" crossOrigin={crossOrigin} precedence="default" />
      ))}
      {scriptSrcs.map(src => (
        <script key={src} type="module" async src={src} crossOrigin={crossOrigin} />
      ))}
    </>
  )
}

// Emits `<meta name="react-app-name">` with the standalone app name the active route was split out of
// (stamped onto each sub-app route's handle by withRepoRouteHandle in repo.ts), so
// `getCurrentReactAppName()` keeps the old per-app telemetry attribution instead of the umbrella
// `repo` app. Falls back to `repo` for routes with no stamp (e.g. the bare owner/repo shell).
function StatsMetaTag() {
  const appName = useSubAppName()
  useEffect(() => {
    updateCurrentApp(appName)
  }, [appName])

  return <meta name="react-app-name" content={appName} />
}

// Re-provides AnalyticsContext with the matched sub-app's name (instead of the umbrella `repo` app
// name every route is registered under). Sub-app packages (e.g. pull-requests, issues) each define
// their own route-level `errorBoundary`s that report to failbot via the ambient AnalyticsContext
// rather than an explicit `reactAppName` — they only get the correct name here because this wraps
// `<Outlet/>`, so it's established *before* any descendant (including one that throws) renders. A
// sub-app's own `AnalyticsWrapper` (mounted deeper, once its content renders successfully) still
// overrides this for non-error analytics events.
function SubAppAnalyticsProvider({children}: {children: ReactNode}) {
  const appName = useSubAppName()
  const ambient = use(AnalyticsContext)

  return (
    <AnalyticsProvider appName={appName} category={ambient?.category ?? ''} metadata={ambient?.metadata ?? {}}>
      {children}
    </AnalyticsProvider>
  )
}

export function RepoLayout() {
  useSyncActiveNavTab()
  useCrossAppNavigationStat()

  const {owner, repo} = useParams()
  usePreloadVisibleRouteComponents(owner, repo)

  if (!owner || !repo) {
    return <Outlet />
  }

  return (
    <>
      <GlobalAssets />
      <StatsMetaTag />
      <Link to={codeViewRepoRoute.generatePath({owner, repo})} hidden id="code-view-repo-link" />
      <SubAppAnalyticsProvider>
        <Outlet />
      </SubAppAnalyticsProvider>
    </>
  )
}
